import { useInfiniteQuery } from "@tanstack/react-query";
import { Entry } from "contentful";
import { useInView } from "framer-motion";
import { useAtomValue } from "jotai";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useEffect, useRef } from "react";

import { sequentialLoadingState } from "~/atoms/sequential-loading";
import { useBreakpoint } from "~/contexts/breakpoint";
import { useConfiguration } from "~/contexts/configuration";
import { usePageTracked } from "~/contexts/page-trackings";
import { useSearch } from "~/contexts/search";
import CrossCountry from "~/types/cross-country";
import Constants from "~/utils/constants";
import { isActiveEntry } from "~/utils/editorial-utils";
import evaUtils from "~/utils/eva-utils";
import TrackingUtils from "~/utils/tracking-utils";

import Container from "../common/container";
import SkeletonProductTile from "../common/skeletons/skeleton-product-tile";
import EditorialEntry from "../editorials/editorial-entry";
import CategoryFilters from "./category-filters";
import styles from "./search-product-grid.module.scss";

const ProductTile = dynamic(() => import("./product-tile"), {
  loading: () => <SkeletonProductTile />,
});

type Props = {
  noResultsComponents?: Entry<unknown>[];
};

export default function SearchProductGrid(props: Props) {
  const t = useTranslations();
  const { eva } = useConfiguration();
  const { sort, query } = useSearch();
  const breakpoint = useBreakpoint();
  const sequentialLoadingReady = useAtomValue(sequentialLoadingState);

  const search = useInfiniteQuery({
    queryKey: ["search", query, sort],
    staleTime: Constants.DEFAULT_PLP_STALE_TIME,
    keepPreviousData: true,
    queryFn: ({ pageParam = 1 }) => evaUtils.getProductsByQueryWithChildren(eva, query, sort, pageParam),
    getNextPageParam: (lastPage) => {
      if (lastPage.Total! > lastPage.Page! * lastPage.PageSize!) {
        return lastPage.Page! + 1;
      }

      return undefined;
    },
  });

  const isPageTracked = usePageTracked();

  const products = search.data?.pages.flatMap((page) => page.Products ?? []) as CrossCountry.Product[];

  const sentinelRef = useRef<HTMLDivElement>(null);

  const sentinelInView = useInView(sentinelRef, { margin: "0% 0% 50% 0%" });
  const stickySentinelInView = useInView(sentinelRef, { margin: "0px 0px -135px 0px" });

  useEffect(() => {
    const usableNetContainer = document.getElementById(Constants.USABLENET_CONTAINER_KEY);

    if (usableNetContainer && breakpoint == "mobile" && sentinelRef.current) {
      if (!stickySentinelInView && sentinelRef.current.getBoundingClientRect().top > 0) {
        usableNetContainer.style.bottom = "65px";
      } else {
        usableNetContainer.style.bottom = "";
      }
    }

    return () => {
      if (usableNetContainer) {
        usableNetContainer.style.bottom = "";
      }
    };
  }, [sequentialLoadingReady.usablenet, breakpoint, stickySentinelInView]);

  // handle infinite scroll
  useEffect(() => {
    if (sentinelInView && search.hasNextPage) {
      search.fetchNextPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelInView]);

  const lastTrackedIndex = useRef(0);
  useEffect(() => {
    lastTrackedIndex.current = isPageTracked
      ? TrackingUtils.trackPLPImpression(products, lastTrackedIndex.current, "search")
      : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageTracked, products]);

  return (
    <>
      <div className={styles.searchedBar}>
        <p>{t("generic.searched")}</p>
        <h1>
          “<span>{query}</span>”
        </h1>
      </div>
      <Container>
        {!!search.data?.pages[0].Total && search.data?.pages[0].Total > 0 ? (
          <div className={styles.prodCount}>
            <p>{t("generic.n_products", { number: search.data?.pages[0].Total })}</p>
          </div>
        ) : (
          <div className={styles.noResults}>
            <h2>{t("generic.noresults")}</h2>
            <p dangerouslySetInnerHTML={{ __html: t("generic.noresultsparagraph") }} />
          </div>
        )}
        <div className={styles.evaGrid}>
          {products?.map((product, idx) => (
            <div key={product.product_id} className={styles.evaTile}>
              <ProductTile product={product} noLazy={idx < Constants.NUMBER_IMAGES_LOADED_WITH_PRIORITY_ON_SEARCH} />
            </div>
          ))}
          <div ref={sentinelRef} aria-hidden="true" />
        </div>
        {!!search.data?.pages[0].Total && search.data?.pages[0].Total > 0 && (
          <div className={styles.filters}>
            <CategoryFilters pageType="inSearchEva" />
          </div>
        )}
      </Container>
      {!search.data?.pages[0].Total &&
        props.noResultsComponents
          ?.filter(isActiveEntry)
          .map((entry) => <EditorialEntry key={entry.sys.id} entry={entry} />)}
    </>
  );
}
